import React, { useLayoutEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useAdminAuth } from '../../context/AdminAuthContext';
import LanguageToggle from '../Common/LanguageToggle';
import { getStoredLanguage, ADMIN_LANGUAGE_STORAGE_KEY } from '../../i18n';
import {
  FiHome,
  FiPackage,
  FiGrid,
  FiShoppingBag,
  FiUsers,
  FiSettings,
  FiLogOut,
  FiMenu,
  FiX,
  FiCoffee,
  FiLayers,
  FiZap,
  FiList,
  FiBox,
  FiSliders,
  FiStar,
} from 'react-icons/fi';

const Container = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #FAF8F3;
`;

const Sidebar = styled.div<{ $isOpen: boolean }>`
  width: 260px;
  background: #1C1511;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.3);
  position: fixed;
  height: 100vh;
  left: 0;
  top: 0;
  z-index: 1000;
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    transform: translateX(${props => props.$isOpen ? '0' : '-100%'});
  }
`;

const SidebarHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid #E8DDD0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Logo = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: #D4A017;
  margin: 0;
  font-family: Georgia, 'Times New Roman', serif;
`;

const MenuToggle = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 24px;
  color: #FAF8F3;
  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
  }
`;

const Navigation = styled.nav`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding: 20px 0;
`;

const NavItem = styled(Link)<{ active?: boolean }>`
  display: flex;
  align-items: center;
  padding: 12px 24px;
  color: ${props => props.active ? '#D4A017' : '#FAF8F3'};
  background-color: ${props => props.active ? 'rgba(212, 160, 23, 0.15)' : 'transparent'};
  text-decoration: none;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: ${props => props.active ? '600' : '500'};
  border-left: 3px solid ${props => props.active ? '#D4A017' : 'transparent'};

  &:hover {
    background-color: rgba(212, 160, 23, 0.1);
    color: #D4A017;
  }

  svg {
    margin-right: 12px;
    font-size: 18px;
  }
`;

const IndentedNavItem = styled(Link)<{ active?: boolean }>`
  display: flex;
  align-items: center;
  padding: 10px 24px 10px 40px;
  color: ${props => props.active ? '#D4A017' : '#FAF8F3'};
  background-color: ${props => props.active ? 'rgba(212, 160, 23, 0.15)' : 'transparent'};
  text-decoration: none;
  transition: all 0.2s ease;
  font-size: 13px;
  font-weight: ${props => props.active ? '600' : '500'};
  border-left: 3px solid ${props => props.active ? '#D4A017' : 'transparent'};

  &:hover {
    background-color: rgba(212, 160, 23, 0.1);
    color: #D4A017;
  }

  svg {
    margin-right: 10px;
    font-size: 16px;
  }
`;

const GroupLabel = styled.div`
  padding: 8px 24px 4px 24px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #E8DDD0;
  margin-top: 8px;
`;

const NavDivider = styled.div`
  height: 1px;
  background: #E8DDD0;
  margin: 8px 16px;
  opacity: 0.3;
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  padding: 12px 24px;
  color: #FAF8F3;
  background-color: transparent;
  text-decoration: none;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 500;
  border: none;
  width: 100%;
  cursor: pointer;

  &:hover {
    background-color: rgba(212, 160, 23, 0.1);
    color: #D4A017;
  }

  svg {
    margin-right: 12px;
    font-size: 18px;
  }
`;

const UserInfo = styled.div`
  padding: 20px 24px;
  border-top: 1px solid #E8DDD0;
  background: #1C1511;
  flex-shrink: 0;
`;

const UserName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #FAF8F3;
  margin-bottom: 4px;
`;

const UserEmail = styled.div`
  font-size: 12px;
  color: #6B5E4E;
  margin-bottom: 12px;
`;

const MainContent = styled.div`
  flex: 1;
  margin-left: 260px;
  padding: 20px;
  background-color: #FAF8F3;

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const TopBar = styled.div`
  background: #F5F0E8;
  padding: 16px 24px;
  border-radius: 8px;
  margin-bottom: 24px;
  border: 1px solid #E8DDD0;
  box-shadow: 0 1px 3px rgba(28, 21, 17, 0.05);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 24px;
  color: #1C1511;
  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
  }
`;

const PageTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1C1511;
  font-family: Georgia, 'Times New Roman', serif;
  margin: 0;
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'block' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;

  @media (min-width: 769px) {
    display: none;
  }
`;

interface NavItemDef {
  path: string;
  label: string;
  icon: React.ReactNode;
}

interface NavGroup {
  groupLabel: string;
  items: NavItemDef[];
}

const AdminLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { adminUser, adminLogout } = useAdminAuth();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  // The admin console and customer storefront share a single i18next
  // instance. If the customer surface most recently changed the shared
  // instance's active language (e.g. the user navigated storefront -> admin
  // within one session), resync to the admin surface's own stored preference
  // on mount so customer language changes never leak into the admin console.
  //
  // This must be a useLayoutEffect (not useEffect), and must force an
  // explicit re-render, rather than relying on react-i18next's own
  // `languageChanged` listener to trigger one. AdminLayout/Header mount
  // fresh on every cross-surface navigation (App.tsx renders them as
  // mutually exclusive Route trees) while both surfaces share one i18next
  // singleton -- so on mount, useTranslation()'s `t` initially resolves
  // against whatever language the *previous* surface last left the
  // singleton in. A plain useEffect only runs after the browser paints,
  // so the first frame would render in the wrong language (a visible
  // flash). useLayoutEffect runs synchronously after DOM mutations but
  // before paint, so correcting the language here prevents the wrong
  // language from ever being painted. react-i18next's own
  // `languageChanged` listener is itself bound from a plain useEffect
  // inside the library, so it is NOT yet registered when this
  // useLayoutEffect synchronously calls i18n.changeLanguage() during the
  // very first mount -- relying on that listener to re-render us would
  // silently leave this surface stuck on the wrong language instead of
  // just flashing it. The explicit forceLanguageResyncRerender() call
  // below is what actually schedules the corrected re-render.
  //
  // This also relies on i18n.changeLanguage() resolving synchronously,
  // which holds today because frontend/src/i18n/index.ts passes
  // `resources` inline at init() with no backend/lazy-loading plugin. If
  // a lazy-loading i18n backend is ever introduced, this fix needs
  // revisiting.
  const [, forceLanguageResyncRerender] = React.useState(0);
  useLayoutEffect(() => {
    const storedLanguage = getStoredLanguage(ADMIN_LANGUAGE_STORAGE_KEY);
    if (storedLanguage !== i18n.language) {
      i18n.changeLanguage(storedLanguage);
      forceLanguageResyncRerender(n => n + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    adminLogout();
    navigate('/admin/login');
  };

  // All nav labels below are wired to t() so the admin sidebar fully
  // localizes; only path/icon values remain static per-item metadata.
  const topNavItem: NavItemDef = { path: '/admin', label: t('admin.nav.dashboard'), icon: <FiHome /> };

  const navGroups: NavGroup[] = [
    {
      groupLabel: t('admin.navGroups.quickBitesAndDrinks'),
      items: [
        { path: '/admin/qb-menu',           label: t('admin.nav.qbMenu'),           icon: <FiList /> },
        { path: '/admin/bubble-tea-options', label: t('admin.nav.bubbleTeaOptions'), icon: <FiCoffee /> },
        { path: '/admin/instant-cakes',      label: t('admin.nav.instantCakes'),     icon: <FiBox /> },
        { path: '/admin/qb-categories',      label: t('admin.nav.qbCategories'),     icon: <FiLayers /> },
        { path: '/admin/qb-orders',          label: t('admin.nav.qbOrders'),         icon: <FiZap /> },
      ],
    },
    {
      groupLabel: t('admin.navGroups.customCakes'),
      items: [
        { path: '/admin/products', label: t('admin.nav.products'), icon: <FiPackage /> },
        { path: '/admin/categories', label: t('admin.nav.categories'), icon: <FiGrid /> },
        { path: '/admin/cake-options', label: t('admin.nav.cakeOptions'), icon: <FiSliders /> },
        { path: '/admin/orders', label: t('admin.nav.orders'), icon: <FiShoppingBag /> },
      ],
    },
    {
      groupLabel: t('admin.navGroups.byoCakes'),
      items: [
        { path: '/admin/byo-options', label: t('admin.nav.byoOptions'), icon: <FiSliders /> },
        { path: '/admin/byo-orders', label: t('admin.nav.byoOrders'), icon: <FiShoppingBag /> },
      ],
    },
  ];

  const bottomNavItems: NavItemDef[] = [
    { path: '/admin/customers', label: t('admin.nav.customers'), icon: <FiUsers /> },
    { path: '/admin/loyalty-points', label: t('admin.nav.loyaltyPoints'), icon: <FiStar /> },
    { path: '/admin/settings', label: t('admin.nav.settings'), icon: <FiSettings /> },
  ];

  const allNavItems: NavItemDef[] = [
    topNavItem,
    ...navGroups.flatMap(g => g.items),
    ...bottomNavItems,
  ];

  const getPageTitle = () => {
    const currentItem = allNavItems.find(item => item.path === location.pathname);
    return currentItem?.label || t('admin.nav.pageTitleFallback');
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <Container>
      <Overlay $isOpen={sidebarOpen} onClick={() => setSidebarOpen(false)} />

      <Sidebar $isOpen={sidebarOpen}>
        <SidebarHeader>
          <Logo>{t('admin.title')}</Logo>
          <MenuToggle onClick={() => setSidebarOpen(false)}>
            <FiX />
          </MenuToggle>
        </SidebarHeader>

        <Navigation>
          {/* Dashboard */}
          <NavItem
            to={topNavItem.path}
            active={isActive(topNavItem.path)}
            onClick={() => setSidebarOpen(false)}
          >
            {topNavItem.icon}
            {topNavItem.label}
          </NavItem>

          {/* Grouped sections */}
          {navGroups.map((group) => (
            <React.Fragment key={group.groupLabel}>
              <NavDivider />
              <GroupLabel>{group.groupLabel}</GroupLabel>
              {group.items.map((item) => (
                <IndentedNavItem
                  key={item.path}
                  to={item.path}
                  active={isActive(item.path)}
                  onClick={() => setSidebarOpen(false)}
                >
                  {item.icon}
                  {item.label}
                </IndentedNavItem>
              ))}
            </React.Fragment>
          ))}

          {/* Bottom items */}
          <NavDivider />
          {bottomNavItems.map((item) => (
            <NavItem
              key={item.path}
              to={item.path}
              active={isActive(item.path)}
              onClick={() => setSidebarOpen(false)}
            >
              {item.icon}
              {item.label}
            </NavItem>
          ))}
        </Navigation>

        <UserInfo>
          {adminUser && (
            <>
              <UserName>{`${adminUser.first_name} ${adminUser.last_name}`}</UserName>
              <UserEmail>{adminUser.email}</UserEmail>
            </>
          )}
          <LogoutButton onClick={handleLogout}>
            <FiLogOut />
            {t('admin.signOut')}
          </LogoutButton>
        </UserInfo>
      </Sidebar>

      <MainContent>
        <TopBar>
          <MobileMenuButton onClick={() => setSidebarOpen(true)}>
            <FiMenu />
          </MobileMenuButton>
          <PageTitle>{getPageTitle()}</PageTitle>
          <LanguageToggle storageKey={ADMIN_LANGUAGE_STORAGE_KEY} />
        </TopBar>
        <Outlet />
      </MainContent>
    </Container>
  );
};

export default AdminLayout;
